'use client';

import { Box, Link, Typography } from '@coolblue-development/freddy';
import selectAvailableStoresPickupTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/selectAvailableStoresPickupTrigger';
import orderBlockSummaryPickUpInStoreTrigger from '@coolblue-development/ga4-events/triggers/OrderBlockSummary/pickUpInStoreTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityStates } from '@coolblue-development/next-availability-state';
import { StoresStockOverlay } from '@coolblue-development/next-stores/client';
import { getNumberOfStoresAvailable } from '@features/product/components/StorePickup/utils/getNumberOfStoresAvailable';
import type { Product } from '@features/product/typings';
import type { Store } from '@features/product-page/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { BLACK_FRIDAY_PRODUCT_IDS } from '../../../../constants/blackFridayProductIds';

type StorePickupPropositionProps = {
  product: Product;
  dateOverride?: string;
  isOrderSummary?: boolean;
  isIphonePreOrder: boolean;
  preferredStore?: Store;
  showBlackFridayPickUpText?: boolean;
};

type StoresStockOverlayProduct = Parameters<typeof StoresStockOverlay>[0]['product'];

export function StorePickupProposition({
  product,
  dateOverride,
  isOrderSummary,
  isIphonePreOrder,
  preferredStore,
  showBlackFridayPickUpText
}: StorePickupPropositionProps) {
  const [open, setOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const { t, Trans } = useTranslation('webshop.productPage.storePickup.propositions');
  const availableForPickupInStore = getNumberOfStoresAvailable(product);

  const getTranslationKey = (availabilityStatus: AvailabilityStates) => {
    if (showBlackFridayPickUpText && BLACK_FRIDAY_PRODUCT_IDS.includes(product.id)) {
      switch (availabilityStatus) {
        case AvailabilityStates.OnStock:
          return 'onStockBlackFriday';
        case AvailabilityStates.PreOrder:
          return isIphonePreOrder ? 'pickItUp' : 'preOrder';
        default:
          return 'default';
      }
    }
    if (preferredStore?.name) {
      switch (availabilityStatus) {
        case AvailabilityStates.OnStock:
          return 'onStockPreferredStore';
        case AvailabilityStates.PreOrder:
          return isIphonePreOrder ? 'pickItUpPreferredStore' : 'preOrderPreferredStore';
        default:
          return 'defaultPreferredStore';
      }
    } else {
      switch (availabilityStatus) {
        case AvailabilityStates.OnStock:
          return 'onStock';
        case AvailabilityStates.PreOrder:
          return isIphonePreOrder ? 'pickItUp' : 'preOrder';
        default:
          return 'default';
      }
    }
  };

  const handleLinkClick = () => {
    setOpen(true);
    trackEvent(
      isOrderSummary
        ? orderBlockSummaryPickUpInStoreTrigger({
            sub_feature: 'pick_up_locations'
          })
        : selectAvailableStoresPickupTrigger()
    );
  };

  return (
    availableForPickupInStore > 0 && (
      <Box>
        <Typography>
          <Trans
            t={t}
            i18nKey={getTranslationKey(product.availabilityState)}
            components={{
              linkComponent: <Link onClick={handleLinkClick} component='button' type='button' />
            }}
            count={availableForPickupInStore}
            {...(preferredStore?.name && {
              values: { preferredStoreName: preferredStore.name }
            })}
          />
        </Typography>

        <StoresStockOverlay
          open={open}
          onClose={() => setOpen(false)}
          product={product as unknown as StoresStockOverlayProduct}
          personalPrice={product.personalPrice}
          timeTravelOverride={dateOverride}
        />
      </Box>
    )
  );
}
